// src/components/admin/ConfirmTierChangeModal.jsx
//
// Confirm dialog for AdminClubDetail. Shows before→after deltas for
// only the fields that actually changed, captures an optional reason,
// and surfaces inline server errors without closing the modal.
//
// Per the spec (02-wireframe.md M1): the modal is the deliberate-write
// moment. Cancel = no callable call. Confirm = fire callable with the
// reason text. On error, modal stays open so Kurt can retry.

import React, { useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const FIELD_LABEL = {
  tier:               'Tier',
  foundingClubNumber: 'Founding Club #',
  trialEndsAt:        'Trial ends',
  seatLimit:          'Seat limit',
};

function renderValue(field, v) {
  if (v == null) return '—';
  if (field === 'trialEndsAt') {
    // v may be a Date, an ISO string, or a Firestore Timestamp-like
    // ({ toDate }). Normalize to a date-only string for display.
    let d;
    if (typeof v === 'string') d = new Date(v);
    else if (v instanceof Date) d = v;
    else if (typeof v.toDate === 'function') d = v.toDate();
    if (!d || Number.isNaN(d.getTime())) return String(v);
    return d.toISOString().slice(0, 10);
  }
  if (field === 'seatLimit' && v === null) return 'unlimited';
  return String(v);
}

export default function ConfirmTierChangeModal({
  clubName,
  deltas,         // [{ field, before, after }] for changed fields only
  inFlight,
  errorMessage,
  onCancel,
  onConfirm,
}) {
  const [reason, setReason] = useState('');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-tier-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
    >
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl border border-zinc-200 shadow-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2
            id="confirm-tier-title"
            className="text-lg font-bold text-zinc-900"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Confirm tier change for {clubName}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={inFlight}
            className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 disabled:opacity-50"
            aria-label="Cancel"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-zinc-700">You're about to change:</p>

          <ul className="space-y-2 text-sm">
            {deltas.map(({ field, before, after }) => (
              <li key={field} className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold text-zinc-900">{FIELD_LABEL[field] || field}:</span>
                <span className="text-zinc-500">{renderValue(field, before)}</span>
                <span className="text-zinc-400">→</span>
                <span className="text-lime-700 font-medium">{renderValue(field, after)}</span>
              </li>
            ))}
          </ul>

          <div>
            <label htmlFor="confirm-reason" className="block text-sm font-medium text-zinc-700">
              Reason (optional)
            </label>
            <textarea
              id="confirm-reason"
              rows={2}
              maxLength={280}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={inFlight}
              autoFocus
              placeholder="e.g. Founding Club #1 onboarding"
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 disabled:bg-zinc-50"
            />
            <p className="mt-1 text-xs text-zinc-500">
              {reason.length}/280 chars. Stored in adminAudit for future reference.
            </p>
          </div>

          {errorMessage && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Couldn't save</p>
                <p>{errorMessage}</p>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-zinc-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={inFlight}
            className="px-4 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={inFlight}
            className="px-4 py-2 rounded-md text-sm font-semibold bg-lime-400 text-zinc-900 hover:bg-lime-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {inFlight ? 'Saving…' : 'Confirm save'}
          </button>
        </div>
      </div>
    </div>
  );
}
